// ----------      START   IMPORT  BLOCK        ----------
import React from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { Trash3, Files } from 'react-bootstrap-icons';
import { useLanguage } from '../../../../../../API/Context/LanguageContext';
import VocabProMenu from '../../../../../../ConfigFiles/multilingual/VocabProMenu';
import VocabDaysOfWeek, { type VocabDaysOfWeekKey } from '../../../../../../ConfigFiles/multilingual/VocabDaysOfWeek';
import { minutesToTime } from '../../../../../../Utils/timeUtils';
// ----------      END   IMPORT  BLOCK        ----------


const DAY_VOCAB_KEYS: VocabDaysOfWeekKey[] = [
  'sun_short', // 0
  'mon_short', // 1
  'tue_short', // 2
  'wed_short', // 3
  'thu_short', // 4
  'fri_short', // 5
  'sat_short', // 6
];

// Display order: Mon (1) -> Sat (6), then Sun (0) at the bottom
const DISPLAY_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

interface OfficeHoursSectionProps {
  officeHours: Record<string, { start: number; end: number }[]>;
  onIntervalChange: (dayStr: string, idx: number, bound: 'start' | 'end', timeStr: string) => void;
  onAddInterval: (dayStr: string) => void;
  onRemoveInterval: (dayStr: string, idx: number) => void;
  onCopyMondayToWeekdays?: () => void;
}

export const OfficeHoursSection: React.FC<OfficeHoursSectionProps> = ({
  officeHours,
  onIntervalChange,
  onAddInterval,
  onRemoveInterval,
  onCopyMondayToWeekdays,
}) => {
  const { t } = useLanguage();

  return (
    <div className="border-top pt-3">
      {/* Header Row: Section Title + Copy Action */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="m-0 fw-bold">{t(VocabProMenu, 'office_hours')}</h6>

        {onCopyMondayToWeekdays && (
          <Button
            variant="outline-secondary"
            size="sm"
            className="py-1 px-2 btn-xs d-flex align-items-center gap-1 shadow-sm"
            onClick={onCopyMondayToWeekdays}
            title="Copy Monday's schedule to Tuesday, Wednesday, Thursday, and Friday"
            style={{ fontSize: '0.75rem' }}
          >
            <Files size={12} />
            <span>Copy Mon → Tue-Fri</span>
          </Button>
        )}
      </div>

      <Row className="g-2">
        {DISPLAY_DAY_ORDER.map((dayNum) => {
          const dayStr = String(dayNum);
          const intervals = officeHours[dayStr] || [];
          const dayVocabKey = DAY_VOCAB_KEYS[dayNum]!;

          return (
            <Col md={12} key={dayNum} className="bg-white p-2 rounded border mb-1">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <span className="fw-bold small">{t(VocabDaysOfWeek, dayVocabKey)}</span>
                
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-none"
                  onClick={() => onAddInterval(dayStr)}
                >
                  + Interval
                </Button>
              </div>

              {intervals.length === 0 ? (
                <p className="text-muted small m-0 fst-italic">Off</p>
              ) : (
                intervals.map((interval, intervalIdx) => (
                  <Row key={intervalIdx} className="g-2 align-items-center mb-1">
                    <Col xs={5}>
                      <Form.Control
                        type="time"
                        size="sm"
                        value={minutesToTime(interval.start)}
                        onChange={(e) =>
                          onIntervalChange(dayStr, intervalIdx, 'start', e.target.value)
                        }
                      />
                    </Col>
                    <Col xs={5}>
                      <Form.Control
                        type="time"
                        size="sm"
                        value={minutesToTime(interval.end)}
                        onChange={(e) =>
                          onIntervalChange(dayStr, intervalIdx, 'end', e.target.value)
                        }
                      />
                    </Col>
                    <Col xs={2} className="text-end">
                      <Button
                        variant="link"
                        className="text-danger p-0"
                        onClick={() => onRemoveInterval(dayStr, intervalIdx)}
                      >
                        <Trash3 size={14} />
                      </Button>
                    </Col>
                  </Row>
                ))
              )}
            </Col>
          );
        })}
      </Row>
    </div>
  );
};